import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, Save, Eye, Undo, Redo, RotateCcw, 
  Palette, Layout, Settings, Shield, FileText,
  Smartphone, Monitor, Clock, Check, X
} from 'lucide-react';
import FileUpload from '../components/FileUpload';
import axios from 'axios';

const BACKEND_URL = '';  // Use relative URLs
const API = '/api';

const ProfileEditor = () => {
  const { profileType } = useParams(); // 'creator' or 'reader'
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  
  // Editor state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [previewMode, setPreviewMode] = useState('desktop'); // 'desktop' or 'mobile'
  
  // Profile data
  const [publishedProfile, setPublishedProfile] = useState(null);
  const [draftProfile, setDraftProfile] = useState(null);
  const [currentData, setCurrentData] = useState(null);
  const [hasDraft, setHasDraft] = useState(false);
  const [lastPublished, setLastPublished] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  
  // Theme and customization data
  const [availableThemes, setAvailableThemes] = useState({});
  const [availableFonts, setAvailableFonts] = useState({});
  
  // Autosave
  const autosaveRef = useRef(null);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  
  // History for undo/redo
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    
    // Validate profile type access
    if (profileType === 'creator' && !['artist', 'author', 'creator'].includes(user.role)) {
      toast.error('Only creators can edit creator profiles');
      navigate('/dashboard');
      return;
    }
    
    fetchEditorData();
    fetchThemes();
    fetchFonts();
  }, [user, profileType, navigate]);

  // Autosave functionality
  useEffect(() => {
    if (currentData && unsavedChanges) {
      // Clear existing timeout
      if (autosaveRef.current) {
        clearTimeout(autosaveRef.current);
      }
      
      // Set new autosave timeout (3 seconds)
      autosaveRef.current = setTimeout(() => {
        saveDraft();
      }, 3000);
    }
    
    return () => {
      if (autosaveRef.current) {
        clearTimeout(autosaveRef.current);
      }
    };
  }, [currentData, unsavedChanges]);

  const fetchEditorData = async () => {
    try {
      const response = await axios.get(`${API}/profile/editor/${profileType}`);
      const data = response.data;
      
      setPublishedProfile(data.published);
      setDraftProfile(data.draft);
      setCurrentData(data.draft || data.published);
      setHasDraft(data.has_draft);
      setLastPublished(data.last_published);
      setLastSaved(data.last_saved);
      
      // Initialize history
      setHistory([data.draft || data.published]);
      setHistoryIndex(0);
      
    } catch (error) {
      console.error('Error fetching editor data:', error);
      toast.error('Failed to load profile editor');
    } finally {
      setLoading(false);
    }
  };

  const fetchThemes = async () => {
    try {
      const response = await axios.get(`${API}/profile/themes`);
      setAvailableThemes(response.data);
    } catch (error) {
      console.error('Error fetching themes:', error);
    }
  };

  const fetchFonts = async () => {
    try {
      const response = await axios.get(`${API}/profile/fonts`);
      setAvailableFonts(response.data);
    } catch (error) {
      console.error('Error fetching fonts:', error);
    }
  };

  const addToHistory = (newData) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newData);
    
    // Limit history size
    if (newHistory.length > 50) {
      newHistory.shift();
    } else {
      setHistoryIndex(historyIndex + 1);
    }
    
    setHistory(newHistory);
  };

  const updateProfile = (updates) => {
    const newData = { ...currentData, ...updates };
    setCurrentData(newData);
    addToHistory(newData);
    setUnsavedChanges(true);
  };

  const updateTheme = (themeUpdates) => {
    const newTheme = { ...currentData.theme, ...themeUpdates };
    updateProfile({ theme: newTheme });
  };

  const updatePrivacySettings = (privacyUpdates) => {
    const newPrivacy = { ...currentData.privacy_settings, ...privacyUpdates };
    updateProfile({ privacy_settings: newPrivacy });
  };

  const saveDraft = async () => {
    if (!currentData) return;
    
    setSaving(true);
    try {
      await axios.post(`${API}/profile/editor/${profileType}/draft`, currentData);
      
      setLastSaved(new Date().toISOString());
      setHasDraft(true);
      setUnsavedChanges(false);
      
      toast.success('Draft saved automatically');
      
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const publishProfile = async () => {
    setPublishing(true);
    try {
      await axios.post(`${API}/profile/editor/${profileType}/publish`);
      
      setPublishedProfile(currentData);
      setHasDraft(false);
      setLastPublished(new Date().toISOString());
      setUnsavedChanges(false);
      
      toast.success('Profile published successfully! 🎉');
      
    } catch (error) {
      console.error('Error publishing profile:', error);
      toast.error('Failed to publish profile');
    } finally {
      setPublishing(false);
    }
  };

  const discardDraft = async () => {
    try {
      await axios.delete(`${API}/profile/editor/${profileType}/draft`);
      
      setCurrentData(publishedProfile);
      setHasDraft(false);
      setUnsavedChanges(false);
      
      // Reset history
      setHistory([publishedProfile]);
      setHistoryIndex(0);
      
      toast.success('Draft discarded');
      
    } catch (error) {
      console.error('Error discarding draft:', error);
      toast.error('Failed to discard draft');
    }
  };

  const undo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setCurrentData(history[newIndex]);
      setHistoryIndex(newIndex);
      setUnsavedChanges(true);
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setCurrentData(history[newIndex]);
      setHistoryIndex(newIndex);
      setUnsavedChanges(true);
    }
  };

  const resetToDefault = () => {
    const defaultTheme = availableThemes.dark_trap || {
      preset: "dark_trap",
      primary_color: "#f97316",
      accent_color: "#ef4444",
      background_style: "solid",
      font_preset: "bold_street"
    };
    
    updateTheme(defaultTheme);
    toast.success('Reset to default theme');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <div className="text-xl font-medium">Loading profile editor...</div>
        </div>
      </div>
    );
  }

  const currentTheme = currentData?.theme || {};
  const themePreset = availableThemes[currentTheme.preset] || availableThemes.dark_trap || {};

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-black/95 backdrop-blur-sm border-b border-gray-800">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Left: Back and Title */}
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate(-1)}
                className="text-gray-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-bold">Profile Editor</h1>
                <div className="flex items-center space-x-2 text-sm text-gray-400">
                  <Badge variant="outline" className="capitalize">
                    {profileType}
                  </Badge>
                  {hasDraft && (
                    <Badge className="bg-blue-600 text-white">
                      <Clock className="w-3 h-3 mr-1" />
                      Draft
                    </Badge>
                  )}
                  {saving && (
                    <Badge className="bg-yellow-600 text-white">
                      Saving...
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Center: Actions */}
            <div className="flex items-center space-x-2">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={undo}
                disabled={historyIndex <= 0}
                className="text-gray-400 hover:text-white"
              >
                <Undo className="w-4 h-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={redo}
                disabled={historyIndex >= history.length - 1}
                className="text-gray-400 hover:text-white"
              >
                <Redo className="w-4 h-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={resetToDefault}
                className="text-gray-400 hover:text-white"
              >
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>

            {/* Right: Publish Actions */}
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1 mr-4">
                <Button
                  variant={previewMode === 'desktop' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setPreviewMode('desktop')}
                >
                  <Monitor className="w-4 h-4" />
                </Button>
                <Button
                  variant={previewMode === 'mobile' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setPreviewMode('mobile')}
                >
                  <Smartphone className="w-4 h-4" />
                </Button>
              </div>
              
              {hasDraft && (
                <Button 
                  variant="outline" 
                  onClick={discardDraft}
                  className="border-gray-600 text-gray-300 hover:bg-gray-800"
                >
                  <X className="w-4 h-4 mr-2" />
                  Discard
                </Button>
              )}
              
              <Button 
                onClick={publishProfile}
                disabled={publishing || !currentData}
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white"
              >
                {publishing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Publishing...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Publish
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Status Bar */}
          <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
            <div className="flex items-center space-x-4">
              {lastSaved && (
                <span>Last saved: {new Date(lastSaved).toLocaleTimeString()}</span>
              )}
              {lastPublished && (
                <span>Last published: {new Date(lastPublished).toLocaleDateString()}</span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {unsavedChanges && (
                <span className="text-yellow-400">Unsaved changes</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Editor Layout */}
      <div className="flex h-[calc(100vh-120px)]">
        {/* Left Panel - Controls */}
        <div className="w-96 bg-gray-900 border-r border-gray-700 overflow-y-auto">
          <Tabs defaultValue="theme" className="w-full">
            <TabsList className="w-full bg-gray-800 border-b border-gray-700 rounded-none h-12">
              <TabsTrigger value="theme" className="flex-1">
                <Palette className="w-4 h-4 mr-2" />
                Theme
              </TabsTrigger>
              <TabsTrigger value="content" className="flex-1">
                <FileText className="w-4 h-4 mr-2" />
                Content
              </TabsTrigger>
              <TabsTrigger value="privacy" className="flex-1">
                <Shield className="w-4 h-4 mr-2" />
                Privacy
              </TabsTrigger>
            </TabsList>

            <div className="p-4 space-y-6">
              {/* Theme Tab */}
              <TabsContent value="theme" className="mt-0 space-y-6">
                {/* Theme Presets */}
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Theme Preset</CardTitle>
                    <CardDescription className="text-gray-400">
                      Choose from our curated dark themes
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(availableThemes).map(([key, theme]) => (
                        <button
                          key={key}
                          onClick={() => updateTheme({ 
                            preset: key,
                            primary_color: theme.primary_color,
                            accent_color: theme.accent_color,
                            background_style: theme.background_style,
                            font_preset: theme.font_preset
                          })}
                          className={`
                            p-3 rounded-lg border-2 transition-all text-left
                            ${currentTheme.preset === key 
                              ? 'border-orange-500 bg-orange-500/10' 
                              : 'border-gray-600 hover:border-gray-500'
                            }
                          `}
                        >
                          <div 
                            className="w-full h-8 rounded mb-2"
                            style={{ 
                              background: theme.background_style === 'gradient' 
                                ? `linear-gradient(135deg, ${theme.primary_color}, ${theme.accent_color})`
                                : theme.primary_color 
                            }}
                          />
                          <div className="text-xs font-medium text-white">{theme.name}</div>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Color Customization */}
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Colors</CardTitle>
                    <CardDescription className="text-gray-400">
                      Customize your theme colors
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="primary-color">Primary Color</Label>
                      <div className="flex items-center space-x-3 mt-2">
                        <input
                          type="color"
                          id="primary-color"
                          value={currentTheme.primary_color || "#f97316"}
                          onChange={(e) => updateTheme({ primary_color: e.target.value })}
                          className="w-10 h-10 rounded-lg border-2 border-gray-600 cursor-pointer"
                        />
                        <Input
                          value={currentTheme.primary_color || "#f97316"}
                          onChange={(e) => updateTheme({ primary_color: e.target.value })}
                          className="bg-gray-700 border-gray-600 text-white flex-1"
                          placeholder="#f97316"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="accent-color">Accent Color</Label>
                      <div className="flex items-center space-x-3 mt-2">
                        <input
                          type="color"
                          id="accent-color"
                          value={currentTheme.accent_color || "#ef4444"}
                          onChange={(e) => updateTheme({ accent_color: e.target.value })}
                          className="w-10 h-10 rounded-lg border-2 border-gray-600 cursor-pointer"
                        />
                        <Input
                          value={currentTheme.accent_color || "#ef4444"}
                          onChange={(e) => updateTheme({ accent_color: e.target.value })}
                          className="bg-gray-700 border-gray-600 text-white flex-1"
                          placeholder="#ef4444"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Font Selection */}
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Typography</CardTitle>
                    <CardDescription className="text-gray-400">
                      Choose your font style
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {Object.entries(availableFonts).map(([key, font]) => (
                        <button
                          key={key}
                          onClick={() => updateTheme({ font_preset: key })}
                          className={`
                            w-full p-3 rounded-lg border-2 transition-all text-left
                            ${currentTheme.font_preset === key 
                              ? 'border-orange-500 bg-orange-500/10' 
                              : 'border-gray-600 hover:border-gray-500'
                            }
                          `}
                        >
                          <div className="font-bold text-white">{font.name}</div>
                          <div className="text-sm text-gray-400">{font.description}</div>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Banner Upload */}
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Banner Image</CardTitle>
                    <CardDescription className="text-gray-400">
                      Upload a custom banner (3000×1000 recommended)
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <FileUpload
                      type="banner"
                      currentUrl={currentData?.banner_url}
                      onUploadComplete={(result) => updateProfile({ banner_url: result.url })}
                      className="w-full"
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Content Tab */}
              <TabsContent value="content" className="mt-0 space-y-6">
                {/* Basic Info */}
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Basic Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="display-name">Display Name</Label>
                      <Input
                        id="display-name"
                        value={currentData?.display_name || ""}
                        onChange={(e) => updateProfile({ display_name: e.target.value })}
                        className="bg-gray-700 border-gray-600 text-white mt-2"
                        placeholder="Your display name"
                        maxLength={50}
                      />
                    </div>

                    <div>
                      <Label htmlFor="bio">Bio (400 characters max)</Label>
                      <Textarea
                        id="bio"
                        value={currentData?.bio_richtext || ""}
                        onChange={(e) => updateProfile({ bio_richtext: e.target.value })}
                        className="bg-gray-700 border-gray-600 text-white mt-2"
                        placeholder="Tell your audience about yourself..."
                        rows={4}
                        maxLength={400}
                      />
                      <div className="text-xs text-gray-500 mt-1">
                        {(currentData?.bio_richtext || "").length}/400 characters
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Social Links */}
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Social Links</CardTitle>
                    <CardDescription className="text-gray-400">
                      Connect your social media profiles
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {['instagram', 'twitter', 'youtube', 'website'].map((platform) => (
                      <div key={platform}>
                        <Label htmlFor={platform} className="capitalize">{platform}</Label>
                        <Input
                          id={platform}
                          value={currentData?.links?.[platform] || ""}
                          onChange={(e) => updateProfile({ 
                            links: { 
                              ...currentData?.links, 
                              [platform]: e.target.value 
                            }
                          })}
                          className="bg-gray-700 border-gray-600 text-white mt-2"
                          placeholder={`https://${platform}.com/yourusername`}
                        />
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Avatar Upload */}
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Profile Avatar</CardTitle>
                    <CardDescription className="text-gray-400">
                      Upload your profile picture (square recommended)
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <FileUpload
                      type="avatar"
                      currentUrl={currentData?.avatar_url}
                      onUploadComplete={async (result) => {
                        updateProfile({ avatar_url: result.url });
                        // Persist to creator profile immediately & update header avatar
                        try {
                          if (result.url) {
                            await axios.put(`${API}/creator/profile/picture`, { profile_image_url: result.url });
                          } else {
                            await axios.delete(`${API}/creator/profile/picture`);
                          }
                          updateUser({ ...user, profile_image_url: result.url || null });
                        } catch (e) {
                          console.error('Failed to persist profile picture:', e);
                        }
                      }}
                      className="mx-auto"
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Privacy Tab */}
              <TabsContent value="privacy" className="mt-0 space-y-6">
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Profile Visibility</CardTitle>
                    <CardDescription className="text-gray-400">
                      Control who can see your profile
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {[
                        { value: 'public', label: 'Public', desc: 'Anyone can view your profile' },
                        { value: 'followers', label: 'Followers Only', desc: 'Only your followers can view' },
                        { value: 'private', label: 'Private', desc: 'Only you can view' }
                      ].map((option) => (
                        <label key={option.value} className="flex items-start space-x-3 cursor-pointer">
                          <input
                            type="radio"
                            name="visibility"
                            value={option.value}
                            checked={(currentData?.privacy_settings?.profile_visibility || 'public') === option.value}
                            onChange={(e) => updatePrivacySettings({ profile_visibility: e.target.value })}
                            className="mt-1"
                          />
                          <div>
                            <div className="text-white font-medium">{option.label}</div>
                            <div className="text-sm text-gray-400">{option.desc}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Fan Wall Settings</CardTitle>
                    <CardDescription className="text-gray-400">
                      Control fan interactions on your profile
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={currentData?.privacy_settings?.allow_fan_wall !== false}
                        onChange={(e) => updatePrivacySettings({ allow_fan_wall: e.target.checked })}
                      />
                      <div>
                        <div className="text-white font-medium">Enable Fan Wall</div>
                        <div className="text-sm text-gray-400">Allow fans to post on your profile</div>
                      </div>
                    </label>

                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={currentData?.privacy_settings?.auto_approve_posts !== false}
                        onChange={(e) => updatePrivacySettings({ auto_approve_posts: e.target.checked })}
                      />
                      <div>
                        <div className="text-white font-medium">Auto-approve Posts</div>
                        <div className="text-sm text-gray-400">Posts appear immediately without approval</div>
                      </div>
                    </label>

                    <div>
                      <Label htmlFor="block-words">Blocked Words (one per line)</Label>
                      <Textarea
                        id="block-words"
                        value={(currentData?.privacy_settings?.block_words || []).join('\n')}
                        onChange={(e) => updatePrivacySettings({ 
                          block_words: e.target.value.split('\n').filter(word => word.trim()) 
                        })}
                        className="bg-gray-700 border-gray-600 text-white mt-2"
                        placeholder="spam&#10;inappropriate&#10;blocked"
                        rows={3}
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </div>
          </Tabs>
        </div>

        {/* Right Panel - Preview */}
        <div className="flex-1 bg-gray-950 overflow-hidden">
          <div className="h-full flex items-center justify-center">
            <div 
              className={`
                bg-black border border-gray-700 overflow-y-auto
                ${previewMode === 'mobile' 
                  ? 'w-96 h-[600px] max-w-sm' 
                  : 'w-full h-full max-w-4xl'
                }
              `}
            >
              {/* Profile Preview Content */}
              <ProfilePreview 
                profileData={currentData} 
                theme={currentTheme}
                profileType={profileType}
                isMobile={previewMode === 'mobile'}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Profile Preview Component
const ProfilePreview = ({ profileData, theme, profileType, isMobile }) => {
  if (!profileData) return null;

  const themeColors = {
    primary: theme.primary_color || '#f97316',
    accent: theme.accent_color || '#ef4444'
  };

  return (
    <div 
      className="min-h-full text-white"
      style={{ 
        background: theme.background_style === 'gradient' 
          ? `linear-gradient(135deg, ${themeColors.primary}20, ${themeColors.accent}20)`
          : '#000000'
      }}
    >
      {/* Banner Section */}
      <div className="relative">
        <div 
          className={`${isMobile ? 'h-32' : 'h-48'} bg-cover bg-center`}
          style={{
            backgroundImage: profileData.banner_url 
              ? `url('${BACKEND_URL}${profileData.banner_url}')`
              : `linear-gradient(135deg, ${themeColors.primary}, ${themeColors.accent})`
          }}
        >
          <div className="absolute inset-0 bg-black/40" />
        </div>
        
        {/* Profile Avatar */}
        <div className="absolute -bottom-8 left-6">
          <div 
            className={`${isMobile ? 'w-16 h-16' : 'w-24 h-24'} rounded-full border-4 border-white bg-gray-800 overflow-hidden`}
          >
            {profileData.avatar_url ? (
              <img 
                src={`${BACKEND_URL}${profileData.avatar_url}`}
                alt={profileData.display_name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div 
                className="w-full h-full flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: themeColors.primary }}
              >
                {(profileData.display_name || 'U').slice(0, 2).toUpperCase()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Profile Info */}
      <div className="pt-12 px-6 pb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold text-white`}>
              {profileData.display_name || 'Your Name'}
            </h1>
            <p className="text-gray-400">@{profileData.handle || 'yourhandle'}</p>
            <Badge 
              className="mt-2 capitalize"
              style={{ backgroundColor: themeColors.primary }}
            >
              {profileType}
            </Badge>
          </div>
          
          <div className="flex space-x-2">
            <Button 
              size="sm"
              style={{ backgroundColor: themeColors.primary }}
              className="text-white"
            >
              Follow
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              className="border-gray-600 text-gray-300"
            >
              Share
            </Button>
          </div>
        </div>

        {/* Bio */}
        {profileData.bio_richtext && (
          <div className="mb-6">
            <p className="text-gray-300 leading-relaxed">
              {profileData.bio_richtext}
            </p>
          </div>
        )}

        {/* Social Links */}
        {profileData.links && Object.keys(profileData.links).length > 0 && (
          <div className="mb-6">
            <h3 className="text-white font-semibold mb-3">Connect</h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(profileData.links).map(([platform, url]) => (
                url && (
                  <Button 
                    key={platform}
                    size="sm" 
                    variant="outline"
                    className="border-gray-600 text-gray-300 capitalize"
                  >
                    {platform}
                  </Button>
                )
              ))}
            </div>
          </div>
        )}

        {/* Widgets Preview */}
        <div className="space-y-6">
          {/* About Widget */}
          <Card className="bg-gray-900 border-gray-700">
            <CardHeader>
              <CardTitle>About</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-300">
                {profileData.bio_richtext || "Tell your audience about yourself..."}
              </p>
            </CardContent>
          </Card>

          {/* Series/Library Widget */}
          {profileType === 'creator' ? (
            <Card className="bg-gray-900 border-gray-700">
              <CardHeader>
                <CardTitle>Featured Series</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="aspect-[3/4] bg-gray-800 rounded-lg">
                      <div className="w-full h-full flex items-center justify-center text-gray-500">
                        Series {i}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-gray-900 border-gray-700">
              <CardHeader>
                <CardTitle>My Library</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="aspect-[3/4] bg-gray-800 rounded-lg">
                      <div className="w-full h-full flex items-center justify-center text-gray-500">
                        Comic {i}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Fan Wall Widget */}
          <Card className="bg-gray-900 border-gray-700">
            <CardHeader>
              <CardTitle>Fan Wall</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="p-3 bg-gray-800 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-6 h-6 bg-gray-700 rounded-full"></div>
                    <span className="text-sm text-gray-400">Fan #1</span>
                  </div>
                  <p className="text-gray-300 text-sm">Amazing work! Love your style! 🔥</p>
                </div>
                <div className="p-3 bg-gray-800 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-6 h-6 bg-gray-700 rounded-full"></div>
                    <span className="text-sm text-gray-400">Fan #2</span>
                  </div>
                  <p className="text-gray-300 text-sm">Can't wait for the next episode!</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProfileEditor;